// src/hooks/useWorkshopQueries.js
// All React Query hooks for the accident/workshop dashboard
// Replace the endpoint strings to match your backend routes

import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "../axiosInstance";
// ─── Derive SLA status from deadline ─────────────────────────────────────────
function deriveSlaStatus(c) {
  if (!c.slaDeadline) return "ON_TIME";
  return new Date(c.slaDeadline) < new Date() && c.currentStatus !== "CASE_CLOSED" && c.currentStatus !== "CLAIM_SETTLED"
    ? "BREACHED" : "ON_TIME";
}

// ─── QUERY KEYS (centralized to avoid typos) ────────────────────────────────
export const KEYS = {
  cases: (filters) => ["cases", filters],
  caseDetail: (id) => ["case", id],
  clients: () => ["clients"],
  documents: (filters) => ["documents", filters],
  workshops: (filters) => ["workshops", filters],
  workshopCases: (workshopId) => ["workshop-cases", workshopId],
  surveyors: (filters) => ["surveyors", filters],
  surveyorsInfinite: (filters) => ["surveyors-infinite", filters],
  surveyAssignments: (filters) => ["survey-assignments", filters],
  caseTimeline: (id) => ["timeline", id],
  slaDashboard: () => ["sla-dashboard"],
  notifications: (filters) => ["notifications", filters],
};

const CASE_STATUS_DATE_FIELD_MAP = {
  WORKSHOP_ASSIGNED: "workshopAssignedDate",
  VEHICLE_PICKED: "driverSentDate",
  VEHICLE_RECEIVED_AT_WORKSHOP: "vehicleEntryDate",
  WORKSHOP_ESTIMATE: "estimateApprovedDate",
  DISMANTLING_APPROVED: "dismantlingApprovedDate",
  DISMANTLING_COMPLETED: "dismantlingCompletedDate",
  SECOND_SURVEY_ASSIGNED:       "workshopVisit2Date",
  REPAIR_APPROVED:              "finalApprovalDate",           
  REPAIR_STARTED:               "workStartedDate",    
  REPAIR_COMPLETED:             "repairCompletedDate",
  REINSPECTION_COMPLETED:       "readyForInspectionDate",
  SECOND_SURVEY_COMPLETED: "workshopVisit2CompletedDate",
  FINAL_SURVEY_COMPLETED: "finalApprovalDate",
  WORK_STARTED: "workStartedDate",
  PART_ORDERED: "partOrderedDate",
  PART_DELIVERED: "partDeliveredDate",
  TRUCK_FINALIZED: "truckFinalizedDate",
  ROAD_TRIAL_COMPLETED: "roadTrialDate",
  READY_FOR_INSPECTION: "readyForInspectionDate",
  VEHICLE_DELIVERED: "vehicleDeliveredDate",
  PAYMENT_REQUEST_SENT: "paymentRequestedDate",
  PAYMENT_COMPLETED: "paymentCompletedDate",
  CLAIM_SETTLED: "claimSettledDate",
  CASE_CLOSED: "caseClosedDate",
};

// ─── CASES ───────────────────────────────────────────────────────────────────

export function useCases(filters = {}) {
  return useQuery({
    queryKey: KEYS.cases(filters),
    staleTime: 0,
    queryFn: async () => {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => v && params.append(k, v));
      const { data } = await api.get(`/api/admin/cases?${params}`);
       const raw = data?.data || data?.cases || [];
      const cases = raw.map(c => ({
        // identity
        id:             c.id,
        accidentCaseNo: c.accidentCaseNo,
        // customer
        customerName:   c.customerName   || null,
        customerPhone:  c.customerNumber || null,
        customerEmail:  c.customerEmail  || null,
        // vehicle
        vehicleNo:      c.vehicleNumber  || null,
        vehicleMake:    c.vehicleMake    || null,
        // accident
        accidentDate:   c.accidentDateTime
                          ? c.accidentDateTime.split("T")[0]
                          : null,
        accidentLocation: c.accidentLocation || null,
        // workflow
        stage:          c.stage,
        currentStatus:  c.currentStatus,
        // assignment
        workshopId:     c.workshopId     || null,
        workshopName:   c.workshopName   || null,
        surveyorName:   c.surveyorName   || null,
        // priority & SLA
        priority:       c.priority       || "Minor",
        slaDeadline:    c.slaDeadline    || null,
        slaStatus:      c.slaStatus      || deriveSlaStatus(c),
        // timestamps
        updatedAt:      c.updatedAt,
        createdAt:      c.createdAt,
        // financials
        policyNumber:   c.policyNumber   || null,
        paymentAmount:  c.paymentAmount  || null,
      }));
      return {
        cases,
        nextCursor: data?.nextCursor || null,
        total:raw.length,
      };
    },
  });
}

export function useCaseDetail(caseId) {
  return useQuery({
    queryKey: KEYS.caseDetail(caseId),
    queryFn: async () => {
      try{
      // caseId here is accidentCaseNo (e.g. 'CPA-SIRTKNGG') — matches the URL param
      const { data } = await api.get(`/api/admin/cases/${caseId}`);
      console.log("Raw case detail response:", data); // Debug log
 
      // Backend returns: { data: { case, details, documents, surveys, timeline } }
      const raw       = data?.data || data;
      console.log("Normalized case detail raw data:", raw); // Debug log
      const c         = raw?.case         || {};
      const details   = raw?.details || null;   // single row from accident_case_details
      const documents = raw?.documents    || [];
      const surveys   = raw?.surveys      || [];
      const services = raw?.services      || [];
      const wi = raw?.workshopIntake || null; // from workshop_case_intake join, if available
      console.log("Case details - case*************:", c);
 
      // ── caseInfo — normalize all field names ────────────────────────────────
      const caseInfo = {
        // identity
        id:               c.id,
        accidentCaseNo:   c.accidentCaseNo,
        // customer
        customerName:     c.customerName     || null,
        customerPhone:    c.customerNumber   || null,   // DB: customerNumber
        customerEmail:    c.email            || c.customerEmail || null,
        // vehicle
        vehicleNo:        c.vehicleNumber    || null,   // DB: vehicleNumber
        vehicleMake:      c.vehicleMake      || null,
        vehicleType:      c.vehicleType      || null,
        // accident
        location:         c.accidentLocation || null,
        accidentDateTime: c.accidentDateTime  || null,   // ← keep raw value too
        accidentDate:     (() => {
                          if (!c.accidentDateTime) return null;
                          const d = new Date(c.accidentDateTime);
                          return d.toLocaleDateString("en-CA"); // "YYYY-MM-DD" in IST
                        })(),
        accidentTime:    (() => {
                          if (!c.accidentDateTime) return null;
                          const d = new Date(c.accidentDateTime);
                          return d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: true });
                        })(),
        accidentPincode:  c.accidentPincode  || null,
        accidentState:    c.accidentState    || null,
        // workflow
        stage:            c.stage,
        currentStatus:    c.currentStatus,
        intimationRaised: !!c.intimationRaised,
        intimationDate:   c.intimationDate || null,
        intimationMode:   c.intimationMode || null,
        intimationReferenceNo: c.intimationReferenceNo || null,
        claimNumber: details?.claimNumber || null,
        // assignment
        workshopId:       c.workshopId       || null,
        workshopName:     c.workshopName     || null,
        estimateApprovedDate:    c.estimateApprovedDate    || null, 
        claimFormDoc:          c.claimFormDoc          || null,  // ← replaces both draft+signed
        claimFormApprovedDate: c.claimFormApprovedDate || null,
        claimFormSentDate:     c.claimFormSentDate     || null,
        surveyorName:     c.surveyorName     || null,
        dismantlingApprovedDate: c.dismantlingApprovedDate || null,
        dismantlingCompletedDate: c.dismantlingCompletedDate || null,
        // insurance (stored on case row)
        policyNo:         c.policyNumber     || null,   // DB: policyNumber
        claimNo:          c.claimNumber      || null,
        workshopEstimateAmount: c.workshopEstimateAmount || null,
        customerPayableAmount: c.customerPayableAmount || null,
        customerPaymentStatus: c.customerPaymentStatus || null,
        workshopPaymentStatus: c.workshopPaymentStatus || null,
        insuranceCompany: details?.insuranceCompany || c.insuranceCompany || null,
        insuranceApprovedAmount: c.insuranceApprovedAmount || null,
        claimStatus : details?.claimStatus || null, 
        // financials
        estimateAmount:   c.estimateAmount   || null,
        paymentAmount:    c.paymentAmount     || null,
        proformaDoc:     c.proformaDoc      || null,
        proformaApprovedDate: c.proformaApprovedDate || null,
        assessmentDoc:    c.assessmentDoc    || null,
        assessmentReceivedDate: c.assessmentReceivedDate || null,
        assessmentCrossCheckedDate: c.assessmentCrossCheckedDate || null,
        taxInvoiceDoc:    c.taxInvoiceDoc    || null,
        taxInvoiceReceivedDate: c.taxInvoiceReceivedDate || null,

        // priority & SLA
        priority:         c.priority         || "Minor",
        slaDeadline:      c.slaDeadline      || null,
        slaStatus:        c.slaStatus        || deriveSlaStatus(c),
        // timestamps
        createdAt:        c.createdAt,
        updatedAt:        c.updatedAt,
      };
 
      // ── driverDetails — from accident_case_details row ──────────────────────
      const driverDetails = details ? {
        name:          details.driverName    || null,
        phone:         details.driverNumber  || null,
        licenseNo:     details.driverDlNumber || null,   // add col if needed
        licenseExpiry: details.driverDlExpiry  || null,
      } : null;
 
      // ── accidentDetails — from accident_case_details row ────────────────────
      const accidentDetails = details ? {
        description:   details.rootCause || details.accidentDescription || null,
        rootCause:     details.rootCause           || null,
        severity:      details.severity            || null,
        policeReportNo:details.firNumber           || null,  // DB: firNumber
        isLoaded:      details.isLoaded,
        thirdPartyLoss:details.thirdPartyLoss,
        vehicleMake:   details.vehicleMake         || null,
        vehicleModel:  details.vehicleModel        || null,
      } : null;
 
      // ── insuranceDetails — mixed: case row + details row ────────────────────
      const insuranceDetails = (c.policyNumber || details?.insuranceCompany) ? {
        company:          details?.insuranceCompany              || c.insuranceCompany || null,
        policyNo:         c.policyNumber                        || null,
        claimNo:          details?.claimNumber                  || null,
        policyType:       details?.policyType                   || null,
        expiryDate:       details?.policyExpiryDate             || null,
        surveyorAllocated:details?.companyRepresentativeName    || null,
        repEmail:         details?.companyRepresentativeEmail   || null,
        repPhone:         details?.companyRepresentativePhone   || null,
      } : null;
 
      // ── documents — normalize field names ────────────────────────────────────
      // DB: documentType, verificationStatus, fileUrl, uploadedBy, uploadedAt
      const normalizedDocuments = documents.map(d => ({
        id:         d.id,
        caseNo:     caseInfo.accidentCaseNo,
        type:       d.documentType,                          // DB: documentType
        status:     d.verificationStatus,                    // DB: verificationStatus
        fileUrl:    d.fileUrl,
        fileName:   d.fileUrl ? d.fileUrl.split("/").pop() : null,
        uploadedBy: d.uploadedBy,
        uploadedAt: d.uploadedAt,
        size:       d.fileSize || null,
        ulipStatus:  d.ulipStatus || null,
        ulipVerified:d.ulipVerified || false,
        ulipExpiryDate: d.ulipExpiryDate || null,
        ulipCheckedAt: d.ulipCheckedAt || null,
      }));
 
      // ── surveys — normalize field names ──────────────────────────────────────
      // DB: surveyType, surveyStatus, createdAt, surveyCompletedDate, remarks
      const normalizedSurveys = surveys.map(s => ({
        id:             s.id,
        type:           s.surveyType,                        // DB: surveyType
        status:         s.surveyStatus,                      // DB: surveyStatus
        surveyorId:     s.surveyorId,
        surveyorName:   s.surveyorName   || null,            // join if needed
        surveyorPhone:  s.surveyorPhone  || null,
        assignedAt:     s.createdAt,                         // DB: createdAt = assigned time
        scheduledAt:    s.scheduledDate  || null,
        completedAt:    s.surveyCompletedDate || null,
        remarks:        s.remarks        || null,
        rejectionReason:s.rejectionReason || null,
        sequence:       s.surveySequence,
      }));
 
      // ── timeline — now included in getCaseById response ──────────────────────
      // DB fields: eventType→status, updatedByRole→role, updatedById→actor
      const normalizedTimeline = (raw?.timeline || []).map(t => ({
        id:          t.id,
        status:      t.eventType,                            // DB: eventType
        description: t.description,
        role:        t.updatedByRole,                        // DB: updatedByRole
        actor:       t.updatedById    || t.updatedByRole,
        timestamp:   t.createdAt,
      }));
 
      // ── workshopInfo — not returned by getCaseById yet ───────────────────────
      // Will come from workshop_case_intake join. For now derive from case row.
      const workshopInfo = c.workshopId ? {
        workshopId:  c.workshopId,
        workshopName:        c.workshopName          || null,
        workshopAssignedDate: c.workshopAssignedDate || null,
        entryDate:   wi?.entryDate || null,
        estimateAmount: wi?.estimateAmount  || null,
        status:      c.currentStatus,
        dismantlingCompleted: !!c.dismantlingCompletedDate,
        inventoryChecked:     !!c.workshopAssignedDate,
        inventoryPdf:         wi?.inventoryPdf || null,
        estimatePdf:          wi?.estimatePdf  || null,
      } : null;
      console.log("Normalized case detail:", { caseInfo, driverDetails, accidentDetails, insuranceDetails, documents: normalizedDocuments, surveys: normalizedSurveys, timeline: normalizedTimeline, workshopInfo });
      
      caseInfo.surveyorName = caseInfo.surveyorName
        || surveys.find(s => s.surveyorName)?.surveyorName
        || null;
        
      return {
        caseInfo,
        driverDetails,
        accidentDetails,
        insuranceDetails,
        documents: normalizedDocuments,
        surveys:   normalizedSurveys,
        services,
        timeline:  normalizedTimeline,
        workshopInfo,
      };
    } catch (err) {
      console.error("Error normalizing case detail:", err);
      throw err;
    }
    },
    enabled: !!caseId,
    refetchOnWindowFocus: true,
    refetchOnMount: "always",
  });
}

export function useUpdateCaseInfo(caseId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (fields) =>
      api.put(`/api/admin/cases/${caseId}/info`, fields).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["case", caseId] });
    },
  });
}

export function useUpdateCaseStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      caseId,
      status,
      currentStatus,
      description,
      role,
      statusDate,
      approvedDate,
      dateField,
      ...extraFields
    }) => {
      const resolvedStatus = status || currentStatus;
      if (!resolvedStatus) {
        throw new Error("status is required to update case status");
      }

      const allowedDateFields = Object.values(CASE_STATUS_DATE_FIELD_MAP);
      const explicitDateField =
        typeof dateField === "string" && allowedDateFields.includes(dateField)
          ? dateField
          : null;
      const mappedDateField = explicitDateField || CASE_STATUS_DATE_FIELD_MAP[resolvedStatus] || null;
      const rawMappedDate = mappedDateField
        ? extraFields[mappedDateField]
        : undefined;
      const resolvedStatusDate = toMySqlDateTime(
        rawMappedDate ?? statusDate ?? approvedDate ?? new Date()
      );

      const payload = {
        status: resolvedStatus,
        description,
        role,
        ...extraFields,
      };

      if (mappedDateField && resolvedStatusDate) {
        payload[mappedDateField] = resolvedStatusDate;
      }

      const { data } = await api.patch(`/api/admin/cases/${caseId}/status`, {
        ...payload,
      });
      return data;
    },
    onSuccess: (_, { caseId }) => {
      qc.invalidateQueries({ queryKey: KEYS.caseDetail(caseId) });
      qc.invalidateQueries({ queryKey: ["cases"] });
    },
  });
}

export function useRaiseIntimation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      caseId,
      mode = "TELEPHONIC",
      intimationDate,
      note,
      referenceNo,
      thirdPartyLoss = false,
      role = "ADMIN",
      ...extraFields
    }) => {
      if (!caseId) {
        throw new Error("caseId is required to raise intimation");
      }

      const payload = {
        status: "INTIMATION_RAISED",
        description: note || `Intimation raised via ${mode}`,
        role,
        intimationRaised: true,
        intimationDate: toMySqlDateTime(intimationDate || new Date()),
        intimationMode: mode,
        thirdPartyLoss: !!thirdPartyLoss,
        ...(referenceNo ? { intimationReferenceNo: referenceNo } : {}),
        ...extraFields,
      };

      const { data } = await api.patch(`/api/admin/cases/${caseId}/status`, payload);
      return data;
    },
    onSuccess: (_, { caseId }) => {
      qc.invalidateQueries({ queryKey: KEYS.caseDetail(caseId) });
      qc.invalidateQueries({ queryKey: ["cases"] });
      qc.invalidateQueries({ queryKey: KEYS.caseTimeline(caseId) });
    },
  });
}

export function useCreateCase() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload) => {
      const { data } = await api.post("/api/admin/cases", payload);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["cases"] }),
  });
}

export function useClients(filters = {}) {
  return useQuery({
    queryKey: [...KEYS.clients(), filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.search !== undefined && filters.search !== "")
        params.append("search", filters.search);
      if (filters.isActive !== undefined)
        params.append("isActive", filters.isActive);
 
      const { data } = await api.get(`/api/admin/clients?${params}`);
 
      // backend returns: { message, data: [...] }
      const raw = data?.data || [];
 
      return raw.map((c) => ({
        id:            c.id,
        clientName:    c.clientName    || c.name  || null,  // support old `name` col too
        clientShort:   c.clientShort              || null,
        contactPerson: c.contactPerson            || null,
        contactPhone:  c.contactPhone             || null,
        primaryEmail:  c.primaryEmail             || null,
        ccEmails:      Array.isArray(c.ccEmails) ? c.ccEmails : [],
        isActive:      c.isActive ?? true,
        activeCases:   Number(c.activeCases  || 0),
        totalCases:    Number(c.totalCases   || 0),
        createdAt:     c.createdAt             || null,
      }));
    },
    staleTime: 1000 * 60 * 10, // 10 min cache
  });
}

export function useCreateClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload) => {
      const { data } = await api.post("/api/admin/clients", {
        clientName:    payload.clientName,
        clientShort:   payload.clientShort   || null,
        contactPerson: payload.contactPerson || null,
        contactPhone:  payload.contactPhone  || null,
        primaryEmail:  payload.primaryEmail  || null,
        ccEmails:      Array.isArray(payload.ccEmails) ? payload.ccEmails : [],
        isActive:      payload.isActive ?? true,
      });
      // backend returns: { message, data: { id, clientName, ... } }
      return data?.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.clients() }),
  });
}

export function useUpdateClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload) => {
      const { id, ...fields } = payload;
      if (!id) throw new Error("id is required to update a client");
 
      // Only send fields that were explicitly provided — avoids overwriting
      // untouched fields with undefined/null on a partial edit.
      const body = {
        ...(fields.clientName    !== undefined && { clientName:    fields.clientName    }),
        ...(fields.clientShort   !== undefined && { clientShort:   fields.clientShort   }),
        ...(fields.contactPerson !== undefined && { contactPerson: fields.contactPerson }),
        ...(fields.contactPhone  !== undefined && { contactPhone:  fields.contactPhone  }),
        ...(fields.primaryEmail  !== undefined && { primaryEmail:  fields.primaryEmail  }),
        ...(fields.ccEmails      !== undefined && { ccEmails:      Array.isArray(fields.ccEmails) ? fields.ccEmails : [] }),
        ...(fields.isActive      !== undefined && { isActive:      fields.isActive      }),
      };
 
      const { data } = await api.patch(`/api/admin/clients/${id}`, body);
      // backend returns: { message, data: { id, clientName, ccEmails[], isActive, ... } }
      return data?.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.clients() }),
  });
}

// ─── DASHBOARD STATS ─────────────────────────────────────────────────────────

export function useDashboardStats() {
  return useQuery({
    queryKey: KEYS.slaDashboard(),
    queryFn: async () => {
      const { data } = await api.get("/api/admin/dashboard/stats");
      console.log("Raw dashboard stats response:", data); // Debug log

      // Normalize response so UI stays stable even if SLA tables are empty.
      const summary = data?.summary || {};

      return {
        kpi: Array.isArray(data?.kpi) ? data.kpi : [],
        pipeline: Array.isArray(data?.pipeline) ? data.pipeline : [],
        recent: Array.isArray(data?.recent) ? data.recent : [],
        slaAlerts: Array.isArray(data?.slaAlerts) ? data.slaAlerts : [],
        timeline: Array.isArray(data?.timeline) ? data.timeline : [],
        summary: {
          activeCases: Number(summary.activeCases) || 0,
          inSurvey: Number(summary.inSurvey) || 0,
          inWorkshop: Number(summary.inWorkshop) || 0,
          pendingDelivery: Number(summary.pendingDelivery) || 0,
          claimsSettled: Number(summary.claimsSettled) || 0,
          slaBreaches: Number(summary.slaBreaches) || 0,
        },
      };
    },
  });
}

// ─── DOCUMENTS ───────────────────────────────────────────────────────────────

export function useDocuments(filters = {}) {
  return useQuery({
    queryKey: KEYS.documents(filters),
    queryFn: async () => {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => v && params.append(k, v));
      const { data } = await api.get(`/api/admin/documents?${params}`);
 
      const raw = data?.data || [];
      const documents = raw.map(d => ({
        id:             d.id,
        caseId:         d.caseId,
        caseNo:         d.accidentCaseNo,         // DB: accidentCaseNo
        customerNumber: d.customerNumber,
        customerEmail:  d.customerEmail,
        vehicleNo:      d.vehicleNumber,          // DB: vehicleNumber
        type:           d.documentType,           // DB: documentType
        status:         d.verificationStatus,     // DB: verificationStatus
        fileUrl:        d.fileUrl,
        fileName:       d.fileUrl
                          ? d.fileUrl.split("/").pop()
                          : null,
        uploadedBy:     d.uploadedBy,
        uploadedAt:     d.uploadedAt,
        size:           d.fileSize || null,
      }));
 
      return {
        documents,
        nextCursor: data?.nextCursor || null,
        total:      data?.total      || raw.length,
        metrics:    data?.metrics    || null,
      };
    },
  });
}
 
export function useVerifyDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ docId, status, remarks }) => {
      const { data } = await api.patch(`/api/admin/documents/${docId}/verify`, {
        status,
        remarks,
      });
      return data;
    },
    onSuccess: () => {
      // Refresh document list and any open case detail page that includes these docs.
      qc.invalidateQueries({ queryKey: ["documents"] });
      qc.invalidateQueries({ queryKey: ["cases"] });
      qc.invalidateQueries({ queryKey: ["case"] }); // all case detail caches
    },
  });
}

// ─── WORKSHOPS ───────────────────────────────────────────────────────────────

export function useWorkshops(filters = {}) {
  return useInfiniteQuery({
    queryKey: [
      "workshops",
      filters.status || "",
      filters.search || "",
      filters.city || "",
      filters.specialization || "",
    ],
    queryFn: async ({ pageParam = null, signal  }) => {
      const params = new URLSearchParams();
      Object.entries({ ...filters, cursor: pageParam }).forEach(([k, v]) => v && params.append(k, v));
      const { data } = await api.get(`/api/admin/workshops?${params}`,{signal});
      const raw = data?.data || [];

      const workshops = raw.map((w) => ({
        // identity — backend uses "id" directly (e.g. "VC-8b8c4ee4-...")
        id:             w.id,
        name:           w.name           || null,
        // contact
        phone:          w.phone          || null,
        email:          w.email          || null,
        // address
        address:        w.address        || null,
        city:           w.city           || null,
        state:          w.state          || null,   // backend returns null; shown as "—" in UI
        pincode:        w.pincode        || null,
        // legal
        gstNo:          w.gstNo          || null,
        // capacity — backend may return null; default to 0 so capacity bar math is safe
        capacity:       w.capacity       ?? 0,
        currentLoad:    w.currentLoad    ?? 0,
        // stats
        activeCases:    w.activeCases    ?? 0,
        totalCases:     w.totalCases     ?? 0,
        rating:         w.rating         ?? 0,
        avgTurnaround:  w.avgTurnaround  ?? 0,
        // meta — backend returns null for these; UI falls back to "—"
        ownerName:      w.ownerName      || null,
        specialization: Array.isArray(w.specialization) ? w.specialization : [],
        status:         w.status         || "ACTIVE",
        joinedAt:       w.joinedAt       || w.createdAt || null,
        lastCaseAt:     w.lastCaseAt     || null,
        // activeCasesList is fetched separately via useWorkshopCases when the
        // detail modal opens — keep empty here so the modal renders safely
        activeCasesList: [],
        // paymentHistory is not returned by this endpoint
        paymentHistory:  [],
      }));
      return {
        workshops,
        metrics:    data?.metrics    || {},
        total:      data?.total      ?? raw.length,
        nextCursor: data?.nextCursor || null,
      };
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    initialPageParam: null,

    staleTime: 1000 * 60 * 5,  // 5 minutes
    gcTime: 1000 * 60 * 30, 
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    placeholderData: (prev) => prev,

    enabled: filters.search
      ? filters.search.length >= 2
      : true,
  });
}

 
// NEW: fetch cases assigned to a specific workshop
// Route: GET /api/workshop/cases/:workshopId
// Called inside WorkshopDetailModal when the "Active Cases" tab is opened.
export function useWorkshopCases(workshopId) {
  return useQuery({
    queryKey: KEYS.workshopCases(workshopId),
    queryFn: async () => {
      const { data } = await api.get(`/api/workshop/cases/${workshopId}`);
 
      // Normalize to the shape used by the Active Cases table in WorkshopDetailModal.
      // Adjust field names below if your backend returns different keys.
      const raw = data?.data || data?.cases || (Array.isArray(data) ? data : []);
      return raw.map((c) => ({
        caseNo:   c.caseNo    || c.accidentCaseNo || c.case_no   || null,
        customer: c.customer  || c.customerName                  || null,
        vehicle:  c.vehicle   || c.vehicleNumber                 || null,
        status:   c.status    || c.currentStatus                 || null,
        // since = date the case was assigned to this workshop
        since:    c.since     || c.assignedAt     || c.createdAt || null,
      }));
    },
    // Only fire when a real workshopId is provided (i.e. the detail modal is open)
    enabled: !!workshopId,
  });
}

export function useUpdateOnSpotServices() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ caseId, services }) => {
      const { data } = await api.patch(
        `/api/admin/cases/${caseId}/on-spot-services`,
        { services }   // array of service type strings, e.g. ["TOWING","FUEL"]
      );
      return data;
    },
    onSuccess: (_, { caseId }) => {
      qc.invalidateQueries({ queryKey: KEYS.caseDetail(caseId) });
      qc.invalidateQueries({ queryKey: ["cases"] });
    },
  });
}

export function useAssignWorkshop() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ caseId, workshopId,workshopName }) => {
      const { data } = await api.post(`/api/admin/cases/${caseId}/assign-workshop`, {
        caseId,
        workshopId,
        workshopName
      });
      return data;
    },
    onSuccess: (_, { caseId }) => {
      qc.invalidateQueries({ queryKey: KEYS.caseDetail(caseId) });
    },
  });
}

export function useCreateWorkshop() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload) => {
      const { data } = await api.post("/api/admin/workshops", payload);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["workshops"] }),
  });
}

export function useAdminFillIntake() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({  caseId, formData }) => {
      const { data } = await api.post(
        `/api/workshop/cases/${caseId}/intake`,
        formData,
        // No Content-Type header — axios sets multipart/form-data + boundary automatically
      );
      return data;
    },
    onSuccess: (_, { caseId }) => {
      qc.invalidateQueries({ queryKey: KEYS.caseDetail(caseId) });
    },
  });
}

// Admin approves the estimate — sets estimateApprovedDate on accident_cases
 
export function useApproveEstimate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ caseId, approvedDate }) => {
      const { data } = await api.patch(`/api/admin/cases/${caseId}/approve-estimate`, {
        approvedDate: approvedDate || new Date().toISOString(),
      });
      return data;
    },
    onSuccess: (_, { caseId }) => {
      qc.invalidateQueries({ queryKey: KEYS.caseDetail(caseId) });
      qc.invalidateQueries({ queryKey: ["cases"] });
    },
  });
}

export function useHandleClaimForm() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ caseId, type, file }) => {
      const formData = new FormData();
      formData.append("type", type);

      if (type === "DRAFT"  && file) formData.append("draftPdf",  file);
      if (type === "SIGNED" && file) formData.append("signedPdf", file);
      // APPROVE and SEND send no file — FormData with just type is fine

      const { data } = await api.post(
        `/api/admin/cases/${caseId}/claim-form`,
        formData
      );
      return data;
    },
    onSuccess: (_, { caseId }) => {
      qc.invalidateQueries({ queryKey: KEYS.caseDetail(caseId) });
    },
  });
}

// ─── SURVEYORS ───────────────────────────────────────────────────────────────

export function useSurveyors(filters = {}) {
  return useQuery({
    queryKey: KEYS.surveyors(filters),
    queryFn: async () => {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => v && params.append(k, v));
      const { data } = await api.get(`/api/admin/surveyors?${params}`);
      return data;
    },
  });
}

const SURVEYOR_PAGE_SIZE = 20;
 
export function useSurveyorsInfinite(filters = {}) {
  const { search, city, insurance } = filters;
  return useInfiniteQuery({
    queryKey: KEYS.surveyorsInfinite({ search, city, insurance }),
    queryFn: async ({ pageParam = null }) => {
      const params = new URLSearchParams();
      if (search)    params.append("search",    search);
      if (city)      params.append("city",      city);
      if (insurance) params.append("insurance", insurance);
      if (pageParam) params.append("cursor",    pageParam);
      params.append("limit", SURVEYOR_PAGE_SIZE);
 
      const { data } = await api.get(`/api/admin/surveyors?${params}`);
 
      // Normalize surveyor fields from backend → what SurveyorsPage expects
      const raw = data?.data || data?.surveyors || [];
  const surveyors = raw.map(s => ({
        id:               s.id,
        surveyorCode:     s.surveyorCode              || null,
        name:             s.fullName                  || null,
        phone:            s.phone                     || null,
        email:            s.email                     || null,
        city:             s.location                  || null,
        state:            null,                                  // no state col in DB yet
        insuranceCompany: s.latestInsuranceCompany    || null,
        licenseNo:        s.SLANo                     || null,
        licenseExpiry:    s.licenceExpiry             || null,
        joinedAt:         s.addedAt                   || null,
        totalSurveys:     parseInt(s.totalSurveys,    10) || 0,
        activeSurveys:    parseInt(s.activeSurveys,   10) || 0,
        completedSurveys: parseInt(s.completedSurveys,10) || 0,
        rejectedSurveys:  parseInt(s.rejectedSurveys, 10) || 0,
        specialization:   [],                                    // no col in DB yet
        category:         s.category                  || null,
        roCode:           s.ROCode                    || null,
        slaNo:            s.SLANo                     || null,
        inliasCode:       s.INLIASCode                || null,
        latitude:         s.latitude                  || null,
        longitude:        s.longitude                 || null,
      }));
 
      return {
        data:       surveyors,
        nextCursor: data?.nextCursor ?? null,
        total:      data?.total      ?? raw.length,
      };
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
  });
}
 
// ─── Survey Assignments query for SurveyorsPage ───────────────────────────────
// Backend: GET /admin/surveys?tab=&search=
// Returns: { data: [...], counts: { ACTIVE, COMPLETED, REJECTED, ALL }, metrics: {...} }
export function useSurveyAssignments(filters = {}) {
  const { tab, search } = filters;
  return useQuery({
    queryKey: KEYS.surveyAssignments({ tab, search }),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (tab)    params.append("tab",    tab);
      if (search) params.append("search", search);
 
      const { data } = await api.get(`/api/admin/assigned-surveyors?${params}`);
 
      const raw = data?.data || data?.surveys || [];
      const surveys = raw.map(s => ({
        id:                  s.id,
        surveyorId:          s.surveyorId,
        surveyorName:        s.surveyorName        || null,
        caseId:              s.caseNo              || s.accidentCaseNo || null,
        vehicleNo:           s.vehicleNo           || s.vehicleNumber  || null,
        surveyType:          s.surveyType,
        surveyStatus:        s.surveyStatus,
        assignedAt:          s.assignedAt          || s.createdAt      || null,
        scheduledDate:       s.scheduledDate       || null,
        surveyCompletedDate: s.surveyCompletedDate || null,
        remarks:             s.remarks             || null,
        rejectionReason:     s.rejectionReason     || null,
        surveySequence:      s.surveySequence      ?? 1,
      }));
 
      return {
        surveys,
        counts:  data?.counts  || computeCounts(surveys),
        metrics: data?.metrics || null,
      };
    },
  });
}
 
// Fallback if backend doesn't return counts
function computeCounts(surveys) {
  return {
    ACTIVE:    surveys.filter(s => ["ASSIGNED","ACCEPTED","RESCHEDULED"].includes(s.surveyStatus)).length,
    COMPLETED: surveys.filter(s => s.surveyStatus === "COMPLETED").length,
    REJECTED:  surveys.filter(s => s.surveyStatus === "REJECTED").length,
    ALL:       surveys.length,
  };
}

function toMySqlDateTime(value) {
  if (value === null || value === undefined) return null;
  const raw = String(value).trim();
  if (!raw) return null;

  // datetime-local from input[type="datetime-local"]
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/.test(raw)) {
    return raw.replace("T", " ") + (raw.length === 16 ? ":00" : "");
  }

  // already in MySQL datetime format
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}(:\d{2})?$/.test(raw)) {
    return raw.length === 16 ? `${raw}:00` : raw;
  }

  // fallback for parseable date strings
  const d = new Date(raw);
  if (!Number.isNaN(d.getTime())) {
    const pad2 = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())} ${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`;
  }

  return raw;
}

export function useAssignSurveyor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ caseId, surveyorId, surveyorName, surveyType, scheduledDate, notes }) => {
      const formattedScheduledDate = toMySqlDateTime(scheduledDate);
      const { data } = await api.post(
        `/api/admin/cases/${caseId}/assign-surveyor`,
        { surveyorId, surveyorName, surveyType, scheduledDate: formattedScheduledDate, notes }
      );
      return data;
    },
    onSuccess: (_, { caseId }) => {
      qc.invalidateQueries({ queryKey: KEYS.caseDetail(caseId) });
    },
  });
}

export function useUpdateSurveyStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ surveyId, status, remarks, rejectionReason, surveyCompletedDate, scheduledAt,scheduledDate, surveyorId, surveyorName }) => {
  const { data } = await api.patch(`/api/admin/surveys/${surveyId}/status`, {
    status,
    remarks:             remarks             || null,
    rejectionReason:     rejectionReason     || null,
    surveyCompletedDate: surveyCompletedDate || null,
    scheduledDate:       scheduledDate       || scheduledAt || null,
    surveyorId:          surveyorId          || null,
    surveyorName:        surveyorName        || null,
  });
      return data;
    },
    onSuccess: () => {
      // Refresh assignments list + any open case detail that shows survey status
      qc.invalidateQueries({ queryKey: ["survey-assignments"] });
      qc.invalidateQueries({ queryKey: ["cases"] });
      qc.invalidateQueries({ queryKey: ["case"] }); 
     
    },
  });
}

export function useHandleProforma() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ caseId, type, file, amount }) => {
      if (type === "UPLOAD") {
        console.log("fileeee", file);
        const formData = new FormData();
        formData.append("type", "UPLOAD");
        if (file) formData.append("proformaDoc", file);
        if (amount) formData.append("proformaAmount", amount);
        const { data } = await api.post(`/api/admin/cases/${caseId}/proforma`, formData);
        return data;
      }
      // APPROVE sends JSON
      const { data } = await api.post(
        `/api/admin/cases/${caseId}/proforma`, 
        { type: "APPROVE" }
      );
      return data;
    },
    onSuccess: (_, { caseId }) => {
      qc.invalidateQueries({ queryKey: KEYS.caseDetail(caseId) });
      qc.invalidateQueries({ queryKey: ["cases"] });
    },
  });
}

export function useHandleAssessment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ caseId, type, file }) => {
      // type: "RECEIVE" | "CROSSCHECK"
      if (type === "RECEIVE") {
        const formData = new FormData();
        formData.append("type", "RECEIVE");
        if (file) formData.append("assessmentDoc", file);
        const { data } = await api.post(
          `/api/admin/cases/${caseId}/assessment`, formData
        );
        return data;
      }
      const { data } = await api.post(
        `/api/admin/cases/${caseId}/assessment`, { type: "CROSSCHECK" }
      );
      return data;
    },
    onSuccess: (_, { caseId }) => {
      qc.invalidateQueries({ queryKey: KEYS.caseDetail(caseId) });
      qc.invalidateQueries({ queryKey: ["cases"] });
    },
  });
}

export function useHandleTaxInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ caseId, file, amount }) => {
      const formData = new FormData();
      if (file) formData.append("taxInvoiceDoc", file);
      if (amount) formData.append("taxInvoiceAmount", amount);
      const { data } = await api.post(
        `/api/admin/cases/${caseId}/tax-invoice`, formData
      );
      return data;
    },
    onSuccess: (_, { caseId }) => {
      qc.invalidateQueries({ queryKey: KEYS.caseDetail(caseId) });
    },
  });
}

export function useCreateSurveyor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload) => {
      const { data } = await api.post("/api/admin/surveyors", payload);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["surveyors"] }),
  });
}

// ─── CLAIMS & PAYMENTS ───────────────────────────────────────────────────────
// All claims are linked to accident_cases via caseId.
// The backend returns raw DB fields — normalization happens here.
//
// Backend routes expected:
//   GET    /admin/claims                        → list with filters
//   PUT    /admin/claims/:caseId/claim-number   → add/edit claim number
//   PUT    /admin/claims/:caseId/status         → update claim status
//   PUT    /admin/claims/:caseId/amounts        → save approved amount + discount
//   POST   /admin/claims/:caseId/notify-customer→ send email/SMS to customer
//   PUT    /admin/claims/:caseId/payment-received→ mark payment received
 
export function useClaims(filters = {}) {
  return useQuery({
    queryKey: ["claims", filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => v != null && v !== "" && params.append(k, v));
      const { data } = await api.get(`/api/admin/claims?${params}`);

      const raw = data?.data || data?.claims || [];

      const claims = raw.map(c => ({
        // ── Identity ──────────────────────────────────────────────────────
        id:              c.id,
        caseNo:          c.caseNo,
        currentStatus:   c.currentStatus  || null,
        stage:           c.stage          || null,

        // ── Customer ──────────────────────────────────────────────────────
        customerName:    c.customerName   || null,
        customerPhone:   c.customerPhone  || null,
        customerEmail:   c.customerEmail  || null,
        vehicleNo:       c.vehicleNo      || null,

        // ── Insurance identity ────────────────────────────────────────────
        insuranceCompany: c.insuranceCompany || null,
        policyNo:         c.policyNo         || null,
        claimNumber:      c.claimNumber      || null,
        claimStatus:      c.claimStatus      || "NO_CLAIM",
        rejectionReason:  c.rejectionReason  || null,
        repName:          c.repName          || null,
        repEmail:         c.repEmail         || null,
        repPhone:         c.repPhone         || null,

        // ── Invoice breakdown ─────────────────────────────────────────────
        taxableAmount:   c.taxableAmount  != null ? Number(c.taxableAmount)  : null,
        gstRate:         c.gstRate        != null ? Number(c.gstRate)        : null,
        gstAmount:       c.gstAmount      != null ? Number(c.gstAmount)      : null,
        towingAmount:    c.towingAmount   != null ? Number(c.towingAmount)   : null,
        totalInvoice:    c.totalInvoice   != null ? Number(c.totalInvoice)   : null,
        // estimateAmount is an alias for totalInvoice — keeps the table column working
        estimateAmount:  c.totalInvoice   != null ? Number(c.totalInvoice)
                       : c.estimateAmount != null ? Number(c.estimateAmount) : null,

        // ── Deduction breakdown ───────────────────────────────────────────
        depreciation:    c.depreciation   != null ? Number(c.depreciation)  : null,
        imt23:           c.imt23          != null ? Number(c.imt23)         : null,
        consumable:      c.consumable     != null ? Number(c.consumable)    : null,
        salvage:         c.salvage        != null ? Number(c.salvage)       : null,
        fileCharge:      c.fileCharge     != null ? Number(c.fileCharge)    : null,
        costToCustomer:  c.costToCustomer != null ? Number(c.costToCustomer): null,
        assessedAmount:  c.assessedAmount != null ? Number(c.assessedAmount): null,

        // ── Discount & partial approval ───────────────────────────────────
        workshopDiscount:  c.workshopDiscount  != null ? Number(c.workshopDiscount)  : null,
        netAfterDiscount:  c.netAfterDiscount  != null ? Number(c.netAfterDiscount)  : null,
        partialApproval:   !!c.partialApproval,
        insApprovedAmount: c.insApprovedAmount != null ? Number(c.insApprovedAmount) : null,
        customerShortfall: c.customerShortfall != null ? Number(c.customerShortfall) : null,

        // ── Final amounts ─────────────────────────────────────────────────
        // approvedAmount = what insurance assessed overall (used in table + modals)
        approvedAmount:  c.approvedAmount  != null ? Number(c.approvedAmount)  : null,
        customerDue:     c.customerDue     != null ? Number(c.customerDue)     : null,
        // paidAmount not returned by backend yet — kept as 0 so modal math is safe
        paidAmount:      c.paidAmount      != null ? Number(c.paidAmount)      : 0,

        // ── Status flags ──────────────────────────────────────────────────
        customerNotified:      !!c.customerNotified,
        customerPaymentStatus: c.customerPaymentStatus || "PENDING",
        workshopPaymentStatus: c.workshopPaymentStatus || "PENDING",
        workshopName:          c.workshopName          || null,

        // ── Key dates ─────────────────────────────────────────────────────
        claimApprovedAt:     c.claimApprovedAt     || null,
        lastStatusCheckedAt: c.lastStatusCheckedAt || null,
        driverSentDate:      c.driverSentDate      || null,
        vehicleDeliveredDate:c.vehicleDeliveredDate|| null,
        paymentRequestedAt:  c.paymentRequestedAt  || null,
        paymentReceivedAt:   c.paymentReceivedAt   || null,
        settledAt:           c.settledAt           || null,
        closedAt:            c.closedAt            || null,

        // ── Misc ──────────────────────────────────────────────────────────
        claimDocumentUrl: c.claimDocumentUrl || null,
        note:             c.note             || null,
        // statusHistory not returned by backend — [] keeps timeline tab safe
        statusHistory:    c.statusHistory    || [],
        createdAt:        c.createdAt,
      }));

      return {
        claims,
        nextCursor: data?.nextCursor || null,
        total:      data?.total      || raw.length,
      };
    },
  });
}
 
// Add or edit claim number (received from insurance company via email)
export function useUpdateClaimNumber() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ caseId, claimNumber }) => {
      const { data } = await api.put(`/api/admin/claims/${caseId}/claim-number`, { claimNumber });
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["claims"] }),
  });
}
 
// Update claim status (admin checks portal / receives email update)
export function useUpdateClaimStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ caseId, status, note, rejectionReason, approvedAt }) => {
      const { data } = await api.put(`/api/admin/claims/${caseId}/status`, {
        status,
        note: note            || null,
        rejectionReason: rejectionReason || null,
        approvedAt: approvedAt      || null,  // date/time insurance approved
      });
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["claims"] }),
  });
}
 
// Save approved amount + workshop discount → backend calculates customerDue
// Normalize frontend aliases to the backend shape before sending.
export function useUpdateClaimAmounts() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload) => {
      const { caseId, ...body } = payload;
      const { data } = await api.put(`/api/admin/claims/${caseId}/amounts`, body);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["claims"] }),
  });
}
 
// Send payment details email/SMS to customer
export function useNotifyCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ caseId, channel, customNote }) => {
      const { data } = await api.post(`/api/admin/claims/${caseId}/notify-customer`, {
        channel,    // "EMAIL" | "SMS" | "BOTH"
        customNote: customNote || null,
      });
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["claims"] }),
  });
}
 
// Mark payment received from insurance company
export function useMarkPaymentReceived() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ caseId, receivedAmount, workshopPaid, note }) => {
      const { data } = await api.put(`/api/admin/claims/${caseId}/payment-received`, {
        receivedAmount: receivedAmount || null,
        workshopPaid:   !!workshopPaid,
        note:           note          || null,
      });
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["claims"] }),
  });
}

export const useMarkWorkshopPaid = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ caseId, amount, note }) =>
      api.post(`/claims/${caseId}/workshop-paid`, { amount, note }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["claims"] }),
  });
};

export const useMarkCustomerPaid = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ caseId, amount,mode, note }) =>
      api.post(`/claims/${caseId}/customer-paid`, { amount, mode, note }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["claims"] }),
  });
};

// Settle claim — final financial settlement
export function useSettleClaim() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ caseId, settlementAmount, settlementDate, remarks }) => {
      const { data } = await api.put(`/api/admin/claims/${caseId}/settle`, {
        settlementAmount,
        settlementDate: settlementDate || null,
        remarks:        remarks        || null,
      });
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["claims"] }),
  });
}
 
// Close case after settlement
export function useCloseCase() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ caseId, closingRemarks }) => {
      const { data } = await api.put(`/api/admin/claims/${caseId}/close`, {
        closingRemarks: closingRemarks || null,
      });
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["claims"] });
      qc.invalidateQueries({ queryKey: ["cases"] });
    },
  });
}

// ─── TIMELINE ────────────────────────────────────────────────────────────────

export function useCaseTimeline(caseId) {
  return useQuery({
    queryKey: KEYS.caseTimeline(caseId),
    queryFn: async () => {
      const { data } = await api.get(`/admin/cases/${caseId}/timeline`);
      return data;
    },
    enabled: !!caseId,
  });
}

// ─── NOTIFICATIONS (for navbar mail icon panel) ──────────────────────────────

export function useNotifications(filters = {}) {
  return useQuery({
    queryKey: KEYS.notifications(filters),
    queryFn: async () => {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => v != null && v !== "" && params.append(k, v));
      const { data } = await api.get(`/api/admin/notifications?${params}`);
      const raw = data?.data || [];
      return {
        notifications: raw.map(n => ({
          id:               n.id,
          caseId:           n.caseId,
          ruleCode:         n.ruleCode         || null,
          notificationType: n.notificationType || "AUTO",
          recipientEmail:   n.sentTo           || null,   // DB column is sentTo
          recipientRole:    n.recipientRole    || null,
          subject:          n.subject          || null,
          sentBy:           n.sentBy           || "SYSTEM",
          emailStatus:      n.emailStatus      || "SENT",
          createdAt:        n.createdAt,
          isRead:           !!n.isRead,
        })),
        total: data?.total || raw.length,
        unreadCount: data?.unreadCount ?? raw.filter(n => !n.isRead).length,
      };
    },
    refetchInterval: 60000, // poll every 60s for new notifications
  });
}

export function useMarkNotificationRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ notificationId, markAll }) => {
      if (markAll) {
        const { data } = await api.patch("/api/admin/notifications/read-all");
        return data;
      }
      const { data } = await api.patch(`/api/admin/notifications/${notificationId}/read`);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["notifications"] });
    },
  });
}

// ─── PER-CASE SLA STATUS ─────────────────────────────────────────────────────

export function useCaseSLA(caseId) {
  return useQuery({
    queryKey: ["caseSLA", caseId],
    queryFn: async () => {
      const { data } = await api.get(`/api/admin/cases/${caseId}/sla`);
      return data?.data || data;
    },
    enabled: !!caseId,
  });
}

// ─── PER-CASE NOTIFICATION / EMAIL LOG ───────────────────────────────────────

export function useCaseNotifications(caseId) {
  return useQuery({
    queryKey: ["caseNotifications", caseId],
    queryFn: async () => {
      const { data } = await api.get(`/api/admin/cases/${caseId}/notifications`);
      const raw = data?.data || [];
      return {
        notifications: raw.map(n => ({
          id:               n.id,
          caseId:           n.caseId,
          ruleCode:         n.ruleCode         || null,
          notificationType: n.notificationType || "AUTO",
          recipientRole:    n.recipientRole    || null,
          subject:          n.subject          || null,
          sentTo:           n.sentTo           || null,
          sentBy:           n.sentBy           || "SYSTEM",
          emailStatus:      n.emailStatus      || "SENT",
          createdAt:        n.createdAt,
          isRead:           !!n.isRead,
        })),
        total: raw.length,
      };
    },
    enabled: !!caseId,
    staleTime: 60000,
  });
}

// ─── TRIGGER MANUAL SLA NOTIFICATION ────────────────────────────────────────

export function useTriggerSLA() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ caseId, ruleCode }) => {
      const { data } = await api.post(`/api/admin/cases/${caseId}/sla/trigger`, { ruleCode });
      return data;
    },
    onSuccess: (_, { caseId }) => {
      qc.invalidateQueries({ queryKey: ["caseNotifications", caseId] });
      qc.invalidateQueries({ queryKey: ["caseSLA", caseId] });
      qc.invalidateQueries({ queryKey: ["notifications"] });
    },
  });
}

// ─── SEND MANUAL EVENT EMAIL ─────────────────────────────────────────────────

export function useSendEventEmail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ caseId, templateKey, recipientRoles, extraData }) => {
      const { data } = await api.post(`/api/admin/cases/${caseId}/send-event-email`, {
        templateKey,
        recipientRoles,
        extraData,
      });
      return data;
    },
    onSuccess: (_, { caseId }) => {
      qc.invalidateQueries({ queryKey: ["caseNotifications", caseId] });
      qc.invalidateQueries({ queryKey: ["notifications"] });
    },
  });
}

export function useSendIntimationEmail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ caseId, to, cc, subject, body, attachDocIds }) => {
      const { data } = await api.post(`/api/admin/cases/${caseId}/send-intimation-email`, {
        to,
        cc,
        subject,
        body,
        attachDocIds,
      });
      return data;
    },
    onSuccess: (_, { caseId }) => {
      qc.invalidateQueries({ queryKey: ["caseNotifications", caseId] });
      qc.invalidateQueries({ queryKey: ["notifications"] });
      qc.invalidateQueries({ queryKey: ["case", caseId] });
    },
  });
}

